import { Injectable } from '@angular/core';
import { CommercialPackageSubscriptionPayload, GetResponse } from '@vi-iot/client';
import { Observable } from 'rxjs';

import { ClientService } from '../../client.service';

import {
  BookingSubscriptionsPlan,
  ContingentSubscription,
  FlatRatePackageSubscriptionPayload,
  InstallationMonetizationFeatures,
  MonetizationApiModel,
  PackageSubscriptionPayload,
  Subscription,
  SubscriptionPlan,
  SubscriptionsDetails,
  SupportedFeatures,
  TrialSubscriptionsList,
  UpdateBookingSubscriptionResponse,
  VoucherValidationDetails,
} from './monetization.model';

@Injectable({ providedIn: 'root' })
export class MonetizationApi implements MonetizationApiModel {
  private readonly baseUrl = 'monetization/v1/vitoguide';

  constructor(private clientService: ClientService) {}

  /**
   * get monetization features for installation
   */
  getMonetizationFeatures(
    installationId: number
  ): Observable<GetResponse<InstallationMonetizationFeatures>> {
    const endpoint = `iot/v1/monetization/installations/${installationId}/monetizationFeatures`;

    return this.clientService.get(endpoint);
  }

  /**
   * subscribe array of boilers to a premium package
   * returns subscription's details if payload contains isSimulation=true
   * @param payload
   */
  subscribeToPackage(
    payload:
      | PackageSubscriptionPayload
      | FlatRatePackageSubscriptionPayload
      | CommercialPackageSubscriptionPayload
  ): Observable<{ subscriptionId: string } | SubscriptionsDetails> {
    const endpoint = `${this.baseUrl}/subscriptions`;

    return this.clientService.post(endpoint, payload);
  }

  /**
   * get all Vitoguide subscriptions
   * @param companyId - required only for Rsa.Default users
   */
  getVitoguideSubscriptions(companyId?: string): Observable<Subscription[]> {
    const endpoint = `${this.baseUrl}/subscriptions`;
    const params = companyId ? { companyId } : {};

    return this.clientService.get(endpoint, params);
  }

  /**
   * get all subscriptions plans with their prices
   */
  getVitoguideSubscriptionsPlans(): Observable<BookingSubscriptionsPlan[]> {
    const endpoint = `${this.baseUrl}/subscriptions/plans`;

    return this.clientService.get(endpoint);
  }

  /**
   * cancel Vitoguide subscription
   * @param cancelReasonCode
   * @param additionalComment
   * @param deviceId
   * @param companyId
   */
  cancelVitoguideSubscription(
    cancelReasonCode: string,
    deviceId: string,
    additionalComment: string,
    companyId?: string
  ): Observable<void> {
    let endpoint = `${this.baseUrl}/subscriptions/${deviceId}`;

    const body = additionalComment ? { cancelReasonCode, additionalComment } : { cancelReasonCode };

    if (companyId) {
      endpoint = endpoint.concat(`?companyId=${companyId}`);
    }

    return this.clientService.delete(endpoint, {}, body);
  }

  /**
   * update Vitoguide subscription
   * returns subscription's details if payload contains isSimulation=true
   * @param payload
   */
  updateVitoguideSubscription(
    payload: PackageSubscriptionPayload
  ): Observable<UpdateBookingSubscriptionResponse[] | SubscriptionsDetails> {
    const endpoint = `${this.baseUrl}/subscriptions/`;

    return this.clientService.patch(endpoint, payload);
  }

  /**
   * create a trial Pro subscription for 6 months
   * @param deviceId
   */
  createTrialSubscription(deviceId: string): Observable<{ deviceId: string }> {
    const endpoint = `${this.baseUrl}/subscriptions/trial`;

    return this.clientService.post(endpoint, { deviceId });
  }

  /**
   * get trial subscriptions
   */
  getTrialSubscriptions(): Observable<TrialSubscriptionsList> {
    const endpoint = `${this.baseUrl}/subscriptions/trial`;

    return this.clientService.get(endpoint);
  }

  // flat rate, Austria, cancel current subscription
  // companyId is optional and for RSA
  cancelFlatRateSubscription(
    cancelReasonCode: string,
    additionalComment: string,
    companyId?: string
  ): Observable<any> {
    let endpoint = `${this.baseUrl}/subscriptions-flat-rate`;
    const body = additionalComment ? { cancelReasonCode, additionalComment } : { cancelReasonCode };
    if (companyId) {
      endpoint += `?companyId=${companyId}`;
    }

    return this.clientService.delete(endpoint, {}, body);
  }

  // Get price details for flat rate subscription
  getFlatRatePriceDetails(): Observable<SubscriptionsDetails> {
    const endpoint = `${this.baseUrl}/subscriptions`;
    const payload: FlatRatePackageSubscriptionPayload = {
      subscriptions: [{ plan: SubscriptionPlan.Pro }],
      isSimulation: true,
    };

    return this.clientService.post(endpoint, payload);
  }

  //TODO To be refactored after BTPD-12437
  getCommercialPriceDetails(
    subscriptionPlan: SubscriptionPlan.Basic | SubscriptionPlan.Pro
  ): Observable<SubscriptionsDetails> {
    const endpoint = `${this.baseUrl}/subscriptions`;
    const payload: CommercialPackageSubscriptionPayload = {
      subscriptions: [{ plan: subscriptionPlan }],
      isSimulation: true,
    };

    return this.clientService.post(endpoint, payload);
  }

  /**
   * get contingent subscriptions
   */
  getContingentSubscriptions(): Observable<ContingentSubscription[]> {
    const endpoint = `${this.baseUrl}/subscriptions/contingents`;

    return this.clientService.get(endpoint);
  }

  getSupportedFeatures(
    companyId: string,
    boilerIds?: string[]
  ): Observable<GetResponse<SupportedFeatures[]>> {
    const endpoint = `${this.baseUrl}/devices/supported-features`;
    const payload = { companyId };

    if (boilerIds) {
      payload['id'] = boilerIds;
    }

    return this.clientService.get(endpoint, payload);
  }

  subscribeVoucherPackage(companyId: string, deviceId: string, voucher: string): Observable<any> {
    const endpoint = `${this.baseUrl}/voucher-subscriptions`;

    return this.clientService.post(endpoint, { companyId, deviceId, voucher });
  }

  validateVoucherPackage(deviceId: string, voucher: string): Observable<VoucherValidationDetails> {
    const endpoint = `${this.baseUrl}/voucher-subscriptions/verify`;

    return this.clientService.post(endpoint, { deviceId, voucher });
  }
}
